var _excluded = ["onChange", "allowMultiple", "value", "defaultValue"],
  _excluded2 = ["id", "value"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import { useState, useCallback, useRef, useLayoutEffect } from 'react';
import { createDescendantContext } from '../descendant';
import { useUnmountEffect, useControllableState, useId } from '../../hooks';
import { addItem, callAllHandlers, createContext, mergeRefs, removeItem, normalizeEventKey, dataAttr, getNextIndex, getPrevIndex } from '../../utils';
var [DescendantsContextProvider,, useDescendants, useDescendant] = createDescendantContext();
export var ListBoxDescendantsContextProvider = DescendantsContextProvider;
export var [ListboxProvider, useListboxContext] = createContext({
  name: 'ListboxContext',
  strict: false
});
export function useListbox(props, ref) {
  var {
      onChange,
      allowMultiple,
      value: valueProp,
      defaultValue: _defaultValue
    } = props,
    htmlProps = _objectWithoutProperties(props, _excluded);
  var [registeredGroupIds, setRegisteredGroupIds] = useState([]);
  var listBoxRef = useRef(null);
  var descendants = useDescendants();

  /**
   * This is used to track the index of the
   * focused listbox item.
   */
  var [focusedIndex, setFocusedIndex] = useState(-1);
  useUnmountEffect(() => {
    setFocusedIndex(-1);
  });
  var [value, setValue] = useControllableState({
    value: valueProp,
    defaultValue: () => {
      if (allowMultiple) return _defaultValue !== null && _defaultValue !== void 0 ? _defaultValue : [];
      return _defaultValue !== null && _defaultValue !== void 0 ? _defaultValue : '-1';
    },
    onChange
  });

  /**
   * Any groups contained within a Listbox, register
   * their unique ids. Listbox will then set aria-owns
   * to these ids
   */
  var registerGroupId = useCallback(id => {
    setRegisteredGroupIds(previousState => [...previousState, id]);
  }, []);
  var unRegisterGroupId = useCallback(idToRemove => {
    setRegisteredGroupIds(previousState => {
      return previousState.filter(id => id !== idToRemove);
    });
  }, []);

  /**
   * if Listbox is scrollable, scroll the Listbox to show
   * the current focused ListboxItem if it is out of view.
   */
  useLayoutEffect(() => {
    if (listBoxRef.current === null || focusedIndex === -1) return;
    var {
      current: listboxElement
    } = listBoxRef;
    var focusedDescendant = descendants.item(focusedIndex);
    if (!focusedDescendant) return;
    var focusedListboxItemElement = focusedDescendant.node;
    if (listboxElement.scrollHeight > listboxElement.clientHeight) {
      var scrollBottom = listboxElement.clientHeight + listboxElement.scrollTop;
      var elementBottom = focusedListboxItemElement.offsetTop + focusedListboxItemElement.offsetHeight;
      if (elementBottom > scrollBottom) {
        listboxElement.scrollTop = elementBottom - listboxElement.clientHeight;
      } else if (focusedListboxItemElement.offsetTop < listboxElement.scrollTop) {
        listboxElement.scrollTop = focusedListboxItemElement.offsetTop;
      }
    }
  }, [focusedIndex, descendants]);
  var getListboxProps = useCallback(function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var getListboxItemId = focusedIndex => {
      if (focusedIndex === -1) return;
      var listboxItem = descendants.item(focusedIndex);
      if (!listboxItem) return;
      return listboxItem.node.id;
    };
    var onBlur = () => {
      setFocusedIndex(-1);
    };
    var onKeyDown = event => {
      var eventKey = normalizeEventKey(event);
      var selectItem = () => {
        var item = descendants.item(focusedIndex);
        if (!item) return;
        item.node.click();
      };
      var keyMap = {
        ArrowDown: () => {
          var nextIndex = getNextIndex(focusedIndex, descendants.count());
          setFocusedIndex(nextIndex);
        },
        ArrowUp: () => {
          var prevIndex = getPrevIndex(focusedIndex, descendants.count());
          setFocusedIndex(prevIndex);
        },
        Enter: selectItem,
        ' ': selectItem
      };
      var action = keyMap[eventKey];
      if (action) {
        event.preventDefault();
        action(event);
      }
    };
    return _objectSpread(_objectSpread({}, props), {}, {
      role: 'listbox',
      tabIndex: 0,
      'aria-activedescendant': getListboxItemId(focusedIndex),
      'aria-multiselectable': allowMultiple,
      'aria-owns': registeredGroupIds.length > 0 ? registeredGroupIds.join(' ') : undefined,
      onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown),
      onBlur: callAllHandlers(props.onBlur, onBlur),
      ref: mergeRefs(listBoxRef, ref)
    });
  }, [focusedIndex, descendants, allowMultiple, listBoxRef, registeredGroupIds, ref]);
  var getListboxItemProps = val => {
    var isSelected = false;
    if (val !== null) {
      isSelected = Array.isArray(value) ? value.includes(val) : value === val;
    }
    var onChange = isSelected => {
      if (val === null) return;
      if (allowMultiple && Array.isArray(value)) {
        var nextState = isSelected ? addItem(value, val) : removeItem(value, val);
        setValue(nextState);
      } else if (isSelected) {
        setValue(val);
      }
    };
    return {
      isSelected,
      onChange
    };
  };
  return {
    descendants,
    getListboxProps,
    getListboxItemProps,
    focusedIndex,
    setFocusedIndex,
    registerGroupId,
    unRegisterGroupId,
    htmlProps
  };
}
export function useListboxItem(props) {
  var {
      id,
      value
    } = props,
    htmlProps = _objectWithoutProperties(props, _excluded2);
  var {
    focusedIndex,
    setFocusedIndex,
    getListboxItemProps
  } = useListboxContext();
  var {
    register,
    index
  } = useDescendant();
  var itemId = useId(id, 'listbox-item');
  var itemRef = useRef(null);
  var {
    isSelected,
    onChange
  } = getListboxItemProps(value);
  var onClick = useCallback(() => {
    onChange === null || onChange === void 0 ? void 0 : onChange(!isSelected);
    setFocusedIndex(index);
  }, [index, setFocusedIndex, isSelected, onChange]);
  var getItemProps = useCallback(function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, props), {}, {
      id: itemId,
      ref: mergeRefs(register, itemRef, ref),
      role: 'option',
      'aria-selected': isSelected,
      'data-focus': dataAttr(index === focusedIndex),
      onClick: callAllHandlers(props.onClick, onClick)
    });
  }, [isSelected, index, focusedIndex, onClick, register, itemId]);
  return {
    isSelected,
    getItemProps,
    htmlProps
  };
}
export var [ListboxGroupProvider, useListboxGroupContext] = createContext({
  name: 'ListboxGroupContext',
  errorMessage: 'useListboxGroupContext: `context` is undefined. Seems you forgot to wrap the Lisbox components in <LisboxGroup />'
});
export function useListboxGroup(props) {
  var {
    id
  } = props;
  var {
    registerGroupId,
    unRegisterGroupId
  } = useListboxContext();
  var groupId = useId(id, !id ? 'listbox-group' : '');
  var generatedTitleId = useId(undefined, 'listbox-group-title');
  var [groupTitleId, setGroupTitleId] = useState();

  /**
   * Register this group with parent Listbox.
   * Parent sets aria-owns with any registered
   * group ids.
   */
  useLayoutEffect(() => {
    registerGroupId(groupId);
    return () => {
      unRegisterGroupId(groupId);
    };
  }, [groupId, registerGroupId, unRegisterGroupId]);
  var getListboxGroupTitleProps = useCallback(function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return _objectSpread({
      id: generatedTitleId,
      setGroupTitleId,
      role: 'presentation'
    }, props);
  }, [generatedTitleId]);
  var getListboxGroupProps = useCallback(() => {
    return {
      id: id !== null && id !== void 0 ? id : groupId,
      role: 'group',
      'aria-labelledby': groupTitleId
    };
  }, [id, groupId, groupTitleId]);
  return {
    getListboxGroupProps,
    getListboxGroupTitleProps,
    htmlProps: props
  };
}